import { db, clearVisitData } from './db';
import type { OutboxItem, OutboxItemType, SyncStatus } from './types';
import { useSyncStore } from './syncStore';
import { isSupabaseConfigured, supabase } from '../auth/supabaseClient';
// 1.18.0 — error classification: permanent vs retryable vs unknown.
import { classifyError } from './errorClassification';
import { STORAGE_BUCKET, getDefaultTemplateForWorkType, getSeedTemplateById, getSeedTemplateByWorkType } from '../../shared/constants';
import { useOrderStore } from '../orders/orderStore';
import type { OrderStatus, WorkOrder, WorkOrderItem } from '../orders/types';
import { statusForVisitOutcome } from '../orders/statusUtils';
import type { VisitSession } from '../visits/types';
import { countSatisfiedChecklistItems } from '../visits/resultUtils';

const MAX_OUTBOX_ATTEMPTS = 5;
const BASE_RETRY_DELAY_MS = 4000;
const MAX_RETRY_DELAY_MS = 60000;
const PROCESSING_STALE_MS = 45000;

let flushPromise: Promise<void> | null = null;
let scheduledFlushTimer: number | null = null;

/**
 * 1.19.8 — Auto-retry background loop.
 *
 * Контекст: до этого `runSyncRecoveryPass` вызывался только при событии
 * `online`. Если первый flush не дожимал всю очередь (например, фото
 * upload завис на 60+ сек и lock протух, или ретраи не дошли), очередь
 * оставалась в processing/failed навсегда — пилот должен был открывать
 * SyncQueue и жать «Запустить синхронизацию» вручную.
 *
 * Этот таймер раз в BACKGROUND_FLUSH_INTERVAL_MS проверяет, есть ли
 * работа в outbox (pending, failed, processing). Если есть и online —
 * запускает `runSyncRecoveryPass` + `flushOutbox`. Recovery pass снимает
 * протухшие лок'и, flush возобновляет работу.
 *
 * Когда очередь пуста — таймер выполняется впустую (1 запрос к Dexie
 * на проверку счётчика). Это дёшево.
 */
const BACKGROUND_FLUSH_INTERVAL_MS = 30_000;
let backgroundFlushTimer: number | null = null;
let syncMetaRequestId = 0;

const NON_BLOCKING_TERMINAL_OUTBOX_TYPES: OutboxItemType[] = ['status_change', 'checklist_result'];
const ACTIVE_MEDIA_STATUSES = ['local', 'uploading', 'failed'] as const;
// Statuses that mean "the technician already closed this visit" — regardless
// of whether the outcome was a clean 'done' or one of the P0-2 follow-up
// states (awaiting_parts, rescheduled, failed_no_access, ...). Used to stop
// blocking outbox flushes on photo/signature uploads for visits the tech
// has already wrapped up.
const VISIT_CLOSED_STATUSES = new Set<OrderStatus>([
  'done',
  'failed',
  'cancelled',
  'awaiting_parts',
  'awaiting_approval',
  'awaiting_follow_up',
  'rescheduled',
  'failed_no_access',
]);

async function isOrderLocallyTerminal(orderId: string) {
  const [order, session] = await Promise.all([
    db.orders.get(orderId),
    db.visit_sessions.get(orderId),
  ]);

  // 1.19.4 — Раньше тут было `|| Boolean(session?.completed_at)`, но это
  // ломает новый протокол закрытия: после markComplete заявка имеет
  // session.completed_at + status='pending_completion'. Если считать её
  // terminal по completed_at, reconcileTerminalOutbox удалит наш свежий
  // status_change(pending_completion) и сломает цепочку.
  //
  // Новое правило: заявка terminal ТОЛЬКО если её серверный статус уже
  // финальный (см. VISIT_CLOSED_STATUSES — pending_completion туда не
  // входит). session.completed_at — теперь сигнал что визит локально
  // закончен, но не означает что серверный flow завершён.
  return order ? VISIT_CLOSED_STATUSES.has(order.status) : false;
}

function isBlockingOutboxItem(item: OutboxItem, isTerminal: boolean) {
  if (!['pending', 'processing', 'failed'].includes(item.status)) return false;
  if (isTerminal && NON_BLOCKING_TERMINAL_OUTBOX_TYPES.includes(item.type)) return false;
  return true;
}

async function hasBlockingOutboxItems(orderId: string) {
  const [items, isTerminal] = await Promise.all([
    db.outbox.where('order_id').equals(orderId).toArray(),
    isOrderLocallyTerminal(orderId),
  ]);

  return items.some((item) => isBlockingOutboxItem(item, isTerminal));
}

async function reconcileTerminalOutbox(orderId: string) {
  if (!(await isOrderLocallyTerminal(orderId))) return;

  const staleIds = (await db.outbox.where('order_id').equals(orderId).toArray())
    .filter((item) => NON_BLOCKING_TERMINAL_OUTBOX_TYPES.includes(item.type))
    .map((item) => item.id);

  if (staleIds.length > 0) {
    await db.outbox.bulkDelete(staleIds);
  }
}

async function getBlockingMediaCounts() {
  const [photos, signatures] = await Promise.all([
    db.photos.where('upload_status').anyOf([...ACTIVE_MEDIA_STATUSES]).toArray(),
    db.signatures.where('upload_status').anyOf([...ACTIVE_MEDIA_STATUSES]).toArray(),
  ]);

  const orderIds = Array.from(new Set([
    ...photos.map((photo) => photo.order_id),
    ...signatures.map((signature) => signature.order_id),
  ]));

  const terminalMap = new Map<string, boolean>();
  await Promise.all(orderIds.map(async (orderId) => {
    terminalMap.set(orderId, await isOrderLocallyTerminal(orderId));
  }));

  const blockingPhotos = photos.filter((photo) => !terminalMap.get(photo.order_id));
  const blockingSignatures = signatures.filter((signature) => !terminalMap.get(signature.order_id));

  return {
    blockingPendingPhotos: blockingPhotos.filter((photo) => photo.upload_status !== 'failed').length,
    blockingFailedPhotos: blockingPhotos.filter((photo) => photo.upload_status === 'failed').length,
    blockingPendingSignatures: blockingSignatures.filter((signature) => signature.upload_status !== 'failed').length,
    blockingFailedSignatures: blockingSignatures.filter((signature) => signature.upload_status === 'failed').length,
  };
}

function getRetryDelay(attempts: number) {
  return Math.min(BASE_RETRY_DELAY_MS * 2 ** Math.max(attempts, 0), MAX_RETRY_DELAY_MS);
}

function scheduleFlush(delayMs = 200) {
  if (scheduledFlushTimer !== null) {
    window.clearTimeout(scheduledFlushTimer);
  }
  scheduledFlushTimer = window.setTimeout(() => {
    scheduledFlushTimer = null;
    void flushOutbox();
  }, delayMs);
}

async function getSyncCounts() {
  const outboxItems = await db.outbox.toArray();

  const groupedOutbox = new Map<string, OutboxItem[]>();
  for (const item of outboxItems) {
    const items = groupedOutbox.get(item.order_id) ?? [];
    items.push(item);
    groupedOutbox.set(item.order_id, items);
  }

  let pendingOutboxCount = 0;
  const blockingFailedOutbox: OutboxItem[] = [];
  const blockingDeadLetterOutbox: OutboxItem[] = [];

  for (const [orderId, items] of groupedOutbox.entries()) {
    const isTerminal = await isOrderLocallyTerminal(orderId);
    const blockingItems = items.filter((item) => isBlockingOutboxItem(item, isTerminal));
    const pendingItems = blockingItems.filter((item) => item.status === 'pending' || item.status === 'processing');
    const failedItems = blockingItems.filter((item) => item.status === 'failed');
    // 1.18.0 — dead_letter обрабатываем отдельно от failed.
    const deadLetterItems = blockingItems.filter((item) => item.status === 'dead_letter');

    if (pendingItems.length > 0) {
      pendingOutboxCount += 1;
    }

    blockingFailedOutbox.push(...failedItems);
    blockingDeadLetterOutbox.push(...deadLetterItems);
  }

  const {
    blockingPendingPhotos,
    blockingFailedPhotos,
    blockingPendingSignatures,
    blockingFailedSignatures,
  } = await getBlockingMediaCounts();

  return {
    pendingCount: pendingOutboxCount + blockingPendingPhotos + blockingPendingSignatures,
    failedCount: blockingFailedOutbox.length + blockingFailedPhotos + blockingFailedSignatures,
    failedOutbox: blockingFailedOutbox,
    deadLetterOutbox: blockingDeadLetterOutbox,
  };
}
async function refreshSyncMeta() {
  const requestId = ++syncMetaRequestId;
  const { pendingCount, failedCount, failedOutbox, deadLetterOutbox } = await getSyncCounts();

  if (requestId !== syncMetaRequestId) {
    return;
  }

  useSyncStore.getState().setPendingCount(pendingCount);
  useSyncStore.getState().setFailed(failedOutbox);
  useSyncStore.getState().setDeadLetter(deadLetterOutbox);

  let nextStatus: SyncStatus;
  if (!navigator.onLine && pendingCount > 0) {
    nextStatus = 'offline';
  } else if (flushPromise) {
    nextStatus = 'syncing';
  } else if (deadLetterOutbox.length > 0 || failedCount > 0) {
    // 1.18.0 — dead_letter тоже приводит status к 'error'. UI разделит
    // отображение через отдельные счётчики из стора.
    nextStatus = 'error';
  } else if (pendingCount > 0) {
    nextStatus = navigator.onLine ? 'pending' : 'offline';
  } else {
    useSyncStore.getState().markSynced();
    return;
  }

  useSyncStore.getState().setStatus(nextStatus);
}

async function getTemplateForOrder(order: WorkOrder | undefined) {
  if (!order) return null;

  if (order.checklist_template_id) {
    const cachedById = await db.templates.get(order.checklist_template_id);
    if (cachedById) return cachedById;
  }

  const cachedByType = await db.templates.where('work_type').equals(order.work_type).toArray();
  if (cachedByType.length > 0) {
    return cachedByType.find((template) => template.is_active) ?? cachedByType[0];
  }

  return getSeedTemplateById(order.checklist_template_id) ?? getSeedTemplateByWorkType(order.work_type) ?? getDefaultTemplateForWorkType(order.work_type);
}

async function buildResultSummary(orderId: string, session: VisitSession | undefined, note: string) {
  const order = await db.orders.get(orderId);
  const template = await getTemplateForOrder(order);
  const photoCount = await db.photos.where('order_id').equals(orderId).count();
  const checklistTotal = template?.items.length ?? Math.max(Object.keys(session?.results ?? {}).length, 0);
  const checklistFilled = template ? countSatisfiedChecklistItems(template.items, session?.results ?? {}) : Object.values(session?.results ?? {}).length;
  const trimmedNote = note.trim();

  const resultSummary = {
    checklist_filled: checklistFilled,
    checklist_total: checklistTotal,
    photo_count: photoCount,
    has_signature: session?.has_signature ?? false,
    signature_refused: session?.signature_refused ?? false,
    technician_note: trimmedNote.length > 140 ? `${trimmedNote.slice(0, 137)}...` : trimmedNote,
    visit_outcome: session?.visit_outcome ?? null,
    outcome_reason: session?.outcome_reason ?? null,
  };

  useOrderStore.getState().setResult(orderId, resultSummary);
  return resultSummary;
}

async function markOrderDoneLocally(item: OutboxItem) {
  const session = await db.visit_sessions.get(item.order_id);
  const resultSummary = await buildResultSummary(item.order_id, session, String(item.payload.note ?? ''));

  // Resolve the post-visit status: prefer the value already enqueued by
  // markComplete() in visitStore (forward-compat), fall back to the legacy
  // mapping based on the local visit session's recorded outcome.
  const payloadStatus = item.payload?.status as OrderStatus | undefined;
  const nextStatus: OrderStatus = payloadStatus ?? statusForVisitOutcome(session?.visit_outcome ?? null);

  await db.orders.update(item.order_id, {
    status: nextStatus,
    completed_at: String(item.payload.completed_at ?? new Date().toISOString()),
    updated_at: new Date().toISOString(),
    result_summary: resultSummary,
  });
}

export async function enqueueOutboxItem(input: { type: OutboxItemType; order_id: string; payload: Record<string, unknown> }) {
  // 1.19.6 — Дедупликация invoice_upsert по order_id.
  //
  // Контекст: invoice_upsert представляет ПОЛНОЕ состояние счёта по
  // заявке (атомарный RPC upsert_invoice_with_items делает upsert
  // invoice + replace всех items). Несколько таких подряд = по сути
  // несколько «снимков» одного и того же документа. Последний из них
  // отменяет предыдущие.
  //
  // До этого фикса каждое из:
  //   • ensureInvoiceFromPlannedItems при старте визита (draft)
  //   • правка позиций в InvoiceScreen
  //   • PaymentScreen (status=paid)
  //   • markComplete (повторно сохраняет финальное состояние)
  // создавало отдельный outbox-айтем. В рантайме мы видели до 3
  // одинаковых invoice_upsert в очереди для одной заявки. Если хоть
  // один падал permanent (как было с total-колонкой), очередь оставалась
  // забитой mertvыми записями.
  //
  // Логика дедупа: при enqueue invoice_upsert ищем pending/failed
  // существующий invoice_upsert с тем же order_id; если есть —
  // удаляем его (он устарел) и кладём новый. dead_letter НЕ трогаем —
  // он остался как след для диагностики, после ручного «Повторить»
  // он перейдёт в pending и тоже будет вытеснен следующим upsert'ом.
  // processing тоже не трогаем — он сейчас в полёте.
  if (input.type === 'invoice_upsert') {
    const stale = await db.outbox
      .where('order_id').equals(input.order_id)
      .and((row) => row.type === 'invoice_upsert' && (row.status === 'pending' || row.status === 'failed'))
      .toArray();
    if (stale.length > 0) {
      await db.outbox.bulkDelete(stale.map((s) => s.id));
    }
  }

  const item: OutboxItem = {
    id: crypto.randomUUID(),
    type: input.type,
    order_id: input.order_id,
    payload: input.payload,
    created_at: new Date().toISOString(),
    status: 'pending',
    attempts: 0,
    last_error: null,
    next_retry_at: null,
    locked_at: null,
  };
  await db.outbox.put(item);
  await refreshSyncMeta();
  if (navigator.onLine) {
    scheduleFlush(150);
  }
}

async function processCreateOrder(item: OutboxItem) {
  const order = item.payload as unknown as WorkOrder;
  // P0-1 stage 2: `items` is a client-side projection of work_order_items
  // rows — there is no column with that name in work_orders. Strip it
  // before sending to PostgREST or the request 400s on "unknown column".
  // The items themselves are sent in a separate create_order_items entry.
  const { items: _items, ...orderRow } = order;
  void _items;
  const { error } = await supabase.from('work_orders').upsert(orderRow, { onConflict: 'id' });
  if (error) throw error;
  await db.orders.put(order);
}

async function processCreateOrderItems(item: OutboxItem) {
  // Payload shape: { items: WorkOrderItem[] }
  const payload = item.payload as { items?: WorkOrderItem[] };
  const items = payload.items ?? [];

  // 1.19.6 — Пустой массив items для create_order_items это АНОМАЛИЯ.
  //
  // Раньше handler делал `if (items.length === 0) return;` и молча
  // удалял outbox-айтем как успешный. Это маскировало серьёзный баг
  // на уровне выше: если по какой-то причине createOrder() отправил
  // в enqueueCreateOrderItems пустой массив (а такого не должно быть —
  // в api.ts:1029 fallback на синтетический item гарантирует >=1),
  // мы об этом никогда не узнавали. Заявка оставалась без items
  // НАВСЕГДА — на сервере parent есть, children никогда не будет.
  //
  // Чиним: при пустом payload — бросаем ошибку с понятным сообщением.
  // Это попадёт в classifyError как permanent (это не сетевая проблема,
  // не RLS — это структурная аномалия), уйдёт в dead_letter. Через
  // SyncQueue Inspector пилот её увидит и сможет прислать в поддержку.
  if (items.length === 0) {
    throw new Error(
      'create_order_items: payload.items пуст. Это аномалия — заявка ' +
      `${item.order_id} осталась без позиций. Проверьте код createOrder в ` +
      'src/domains/orders/api.ts (fallback на синтетический item).',
    );
  }

  // Strip client-only / view-only fields. result_summary is initialised null
  // by the dispatcher's create flow and updated later by the technician
  // during stage 3; the column exists, so it's safe to send as null.
  const rows = items.map((i) => ({
    id: i.id,
    work_order_id: i.work_order_id,
    work_type: i.work_type,
    service_type_id: i.service_type_id,
    checklist_template_id: i.checklist_template_id,
    title: i.title,
    status: i.status,
    outcome: i.outcome,
    outcome_reason: i.outcome_reason,
    result_summary: i.result_summary,
    price_planned: i.price_planned,
    price_amount: i.price_amount,
    price_adjustment_reason: i.price_adjustment_reason,
    started_at: i.started_at,
    completed_at: i.completed_at,
    position: i.position,
    created_at: i.created_at,
    updated_at: i.updated_at,
  }));

  // Use upsert(id) so a retried outbox flush (e.g. flush ran twice on the
  // same item due to a transient error) doesn't produce duplicate rows.
  const { error } = await supabase
    .from('work_order_items')
    .upsert(rows, { onConflict: 'id' });
  if (error) throw error;
}

/**
 * 1.19.7 — Атомарное создание заявки + items через RPC.
 *
 * Заменяет пару `processCreateOrder` + `processCreateOrderItems` для
 * новых заявок. Решает гонку RLS: в старой схеме создание parent и
 * children — два независимых PostgREST-запроса, между которыми
 * pgbouncer мог выдать разные backend-соединения. RLS политика
 * work_order_items.insert (with check ... in (select id from
 * work_orders)) могла не увидеть только что вставленный parent →
 * permanent error → dead_letter → пилот жал «Повторить» вручную.
 *
 * Теперь это один RPC `create_order_with_items` — серверная функция
 * с одной транзакцией: parent и children записываются вместе, RLS
 * проверка детей видит parent в той же транзакции.
 *
 * Payload shape: { order: WorkOrder, items: WorkOrderItem[] }.
 *
 * Идемпотентность: RPC использует upsert по id для обеих таблиц. Если
 * вызов упал на сети ПОСЛЕ серверного коммита — повтор будет no-op.
 */
async function processCreateOrderWithItems(item: OutboxItem) {
  const payload = item.payload as { order?: WorkOrder; items?: WorkOrderItem[] };
  const order = payload.order;
  const items = payload.items ?? [];

  if (!order) {
    throw new Error(
      `create_order_with_items: payload.order отсутствует для outbox ${item.id}. ` +
      'Аномалия структуры — это не должно происходить.',
    );
  }

  // Strip client-only поля из order (items в JSONB передаём отдельно).
  // RPC ожидает order как plain JSONB без поля items (оно лишнее
  // в work_orders таблице) и helpers (это отдельная таблица).
  const { items: _items, helpers: _helpers, ...orderRow } = order;
  void _items;
  void _helpers;

  // Готовим items для RPC. Очищаем от client-only и view-only полей.
  const itemsForRpc = items.map((i) => ({
    id: i.id,
    work_type: i.work_type,
    service_type_id: i.service_type_id,
    checklist_template_id: i.checklist_template_id,
    title: i.title,
    status: i.status,
    outcome: i.outcome,
    outcome_reason: i.outcome_reason,
    result_summary: i.result_summary,
    price_planned: i.price_planned,
    price_amount: i.price_amount,
    price_adjustment_reason: i.price_adjustment_reason,
    started_at: i.started_at,
    completed_at: i.completed_at,
    position: i.position,
    created_at: i.created_at,
    updated_at: i.updated_at,
  }));

  const { error } = await supabase.rpc('create_order_with_items', {
    p_order: orderRow,
    p_items: itemsForRpc,
  });
  if (error) throw error;

  // Локально подтверждаем что order лежит. Безусловно — повторный
  // вызов processCreateOrderWithItems после успеха ничего не сломает
  // (db.orders.put идемпотентен).
  await db.orders.put(order);
}

/**
 * P0-1 stage 3 — partial update of a single work_order_items row.
 *
 * Used when the technician closes an item: writes status + outcome
 * + price_amount + price_adjustment_reason + completed_at. The payload
 * is a sparse patch — only fields present go to the server.
 */
async function processUpdateWorkOrderItem(item: OutboxItem) {
  const payload = item.payload as { item_id?: string; patch?: Record<string, unknown> };
  const itemId = payload.item_id;
  const patch = payload.patch ?? {};
  if (!itemId) return;
  const { error } = await supabase
    .from('work_order_items')
    .update(patch)
    .eq('id', itemId);
  if (error) throw error;
}

async function processStatusChange(item: OutboxItem) {
  const patch = item.payload;
  const { error } = await supabase.from('work_orders').update(patch).eq('id', item.order_id);
  if (error) throw error;
  await db.orders.update(item.order_id, patch);
  await reconcileTerminalOutbox(item.order_id);
}

async function processChecklistResult(item: OutboxItem) {
  // Sync the current checklist progress snapshot to the server.
  // Each checklist_result outbox item carries a single item's result in its
  // payload, but we upsert the full session.results map so the server always
  // sees a consistent snapshot rather than individual deltas.
  // completed_at is left to the DB default (NOW()) for in-progress visits —
  // processVisitComplete will overwrite it with the real timestamp on finish.
  const session = await db.visit_sessions.get(item.order_id);
  if (!session) {
    // Session was already cleared (visit finalized) — nothing to sync.
    return;
  }

  const { error } = await supabase.from('visit_results').upsert(
    {
      order_id: item.order_id,
      checklist_data: Object.values(session.results ?? {}),
    },
    { onConflict: 'order_id' },
  );
  if (error) throw error;
}

async function processVisitComplete(item: OutboxItem) {
  const session = await db.visit_sessions.get(item.order_id);
  const signature = await db.signatures.get(item.order_id);
  const photoCount = await db.photos
    .where('order_id')
    .equals(item.order_id)
    .count();
  const resultSummary = await buildResultSummary(item.order_id, session, String(item.payload.note ?? ''));

  // Same status-resolution rule as markOrderDoneLocally(): trust the payload
  // first, fall back to mapping the session's outcome. Keeps server-side
  // status consistent with what the technician's UI actually showed.
  const payloadStatus = item.payload?.status as OrderStatus | undefined;
  const nextStatus: OrderStatus = payloadStatus ?? statusForVisitOutcome(session?.visit_outcome ?? null);

  // photo_urls is intentionally empty here — photos are uploaded AFTER JSON outbox items.
  // patchUploadedMedia() in finalizeConfirmedVisits() populates photo_urls once uploads complete.
  const { error: visitError } = await supabase.from('visit_results').upsert({
    order_id: item.order_id,
    checklist_data: Object.values(session?.results ?? {}),
    photo_urls: [],
    signature_url: signature && !signature.refused ? signature.server_url : null,
    signature_refused: session?.signature_refused ?? false,
    refuse_reason: session?.refuse_reason ?? null,
    technician_note: String(item.payload.note ?? '').trim(),
    completed_at: String(item.payload.completed_at),
  });
  if (visitError) throw visitError;

  const patch: Partial<WorkOrder> = {
    status: nextStatus,
    completed_at: String(item.payload.completed_at ?? new Date().toISOString()),
    updated_at: new Date().toISOString(),
    result_summary: resultSummary,
  };
  // 1.12.1 — when the payload explicitly clears help_requested/comment
  // (used by abortVisit to drop the SOS flag when closing as failed),
  // pass that through to the server patch. Without this, the SOS marker
  // would persist on a closed order.
  if ('help_requested' in (item.payload ?? {})) {
    (patch as Record<string, unknown>).help_requested = item.payload.help_requested ?? null;
  }
  if ('help_comment' in (item.payload ?? {})) {
    (patch as Record<string, unknown>).help_comment = item.payload.help_comment ?? null;
  }
  const { error: orderError } = await supabase.from('work_orders').update(patch).eq('id', item.order_id);
  if (orderError) throw orderError;
  await db.orders.update(item.order_id, patch);
  await reconcileTerminalOutbox(item.order_id);
}

// ── 1.19.4 — Новый протокол закрытия визита ─────────────────────────────
//
// Закрытие визита больше не атомарная операция в одном handler'е —
// это последовательность outbox-items, заканчивающаяся finalize_visit
// который атомарно (через RPC) пишет visit_results и финальный статус.
//
// Цепочка enqueue (в visitStore.markComplete):
//   1. status_change → pending_completion (быстро, диспетчер видит сразу)
//   2. update_work_order_item × N (статусы/outcome/цены по items)
//   3. invoice_upsert (если visit_outcome требует оплаты)
//   4. media_finalize (барьер — ждёт фото и подпись)
//   5. finalize_visit (RPC — атомарный коммит)
//
// Каждый шаг идёт через ту же outbox-инфраструктуру с retry/dead_letter.
// Барьеры обеспечиваются через computeOutboxBlockingDeps (см. ниже).

/**
 * media_finalize handler — барьер на медиа.
 *
 * Не отправляет данные на сервер сам по себе. Проверяет, что все
 * db.photos с этим order_id в upload_status='uploaded' и
 * db.signatures либо uploaded либо refused (refused не требует blob/upload).
 *
 * Если что-то не uploaded — пробует загрузить здесь же. Если фото blob
 * пропал (storage clear, crash), помечаем как failed без blob — это
 * permanent state, пилот должен пересделать или закрыть как failed.
 *
 * Возврат:
 *   • Все uploaded → handler возвращается без ошибки, элемент удаляется
 *     из outbox, разблокирует finalize_visit.
 *   • Какое-то медиа не догрузилось → throw retryable, на след. flush повтор.
 *   • blob потерян безвозвратно → throw permanent ('media_lost'), идём
 *     в dead_letter, диспетчер увидит проблему.
 */
async function processMediaFinalize(item: OutboxItem) {
  const orderId = item.order_id;

  const photos = await db.photos.where('order_id').equals(orderId).toArray();
  const signature = await db.signatures.get(orderId);

  // Список того, что ещё не uploaded.
  const pendingPhotos = photos.filter((p) => p.upload_status !== 'uploaded');
  // Подпись считается готовой если она uploaded ИЛИ refused (отказ не
  // требует blob — visit_results.signature_refused=true, signature_url=null).
  const signaturePending = signature
    ? signature.upload_status !== 'uploaded' && !signature.refused
    : false;

  if (pendingPhotos.length === 0 && !signaturePending) {
    // Всё уже готово — handler успешно завершён, барьер пройден.
    return;
  }

  // Что-то не uploaded. Пробуем догрузить здесь же — это синхронно
  // относительно outbox flush'а, не плодим параллельных загрузок.
  for (const photo of pendingPhotos) {
    if (!photo.blob && !photo.server_url) {
      // Blob потерян (storage clear, crash, корректно прерванная сессия).
      // Восстановить невозможно — permanent.
      throw Object.assign(new Error(`Фото ${photo.id} потеряло данные, загрузка невозможна`), {
        code: '23000', // условный PG-код для permanent классификации
        status: 422,
      });
    }
    try {
      await db.photos.update(photo.id, { upload_status: 'uploading' });
      await uploadPhoto(photo);
    } catch (error) {
      // Возвращаем статус что-бы следующий заход тоже пробовал.
      await db.photos.update(photo.id, {
        upload_status: 'failed',
        attempts: photo.attempts + 1,
        blob: photo.blob,
      });
      // Прокидываем ошибку — handler упадёт, retry повторит media_finalize
      // через outbox-loop. classifyError разберёт retryable/permanent.
      throw error;
    }
  }

  if (signaturePending && signature) {
    if (!signature.blob && !signature.refused && !signature.server_url) {
      throw Object.assign(new Error(`Подпись по заявке ${orderId} потеряла данные`), {
        code: '23000',
        status: 422,
      });
    }
    try {
      await db.signatures.update(signature.order_id, { upload_status: 'uploading' });
      await uploadSignature(signature);
    } catch (error) {
      await db.signatures.update(signature.order_id, {
        upload_status: 'failed',
        attempts: (signature.attempts ?? 0) + 1,
      });
      throw error;
    }
  }

  // После успешного догруза — повторно проверим. Если что-то ещё не
  // готово (race с параллельным изменением — крайне маловероятно), кинем
  // retryable чтобы следующий flush снова попробовал.
  const photosAfter = await db.photos.where('order_id').equals(orderId).toArray();
  const stillPending = photosAfter.some((p) => p.upload_status !== 'uploaded');
  const signatureAfter = await db.signatures.get(orderId);
  const stillSigPending = signatureAfter
    ? signatureAfter.upload_status !== 'uploaded' && !signatureAfter.refused
    : false;

  if (stillPending || stillSigPending) {
    throw new Error('media_finalize: некоторые медиа всё ещё не uploaded после попытки догрузки');
  }
  // Всё ок — барьер пройден, handler удалит этот outbox item.
}

/**
 * finalize_visit handler — атомарный коммит закрытия визита через RPC.
 *
 * Должен быть последним в цепочке (см. computeOutboxBlockingDeps). К
 * моменту вызова на сервере уже:
 *   • work_orders.status = pending_completion (от status_change шага 1)
 *   • work_order_items обновлены (от update_work_order_item шагов 2)
 *   • invoices + invoice_items записаны (от invoice_upsert шага 3)
 *   • storage содержит все файлы (от media_finalize шага 4)
 *
 * Этот handler:
 *   1. Собирает payload из локальной БД (visit_session, photos, signature).
 *   2. Вызывает RPC finalize_visit_rpc.
 *   3. При успехе обновляет локальный db.orders.status в финальный
 *      target и orderStore (UI техника увидит финальный статус).
 *   4. Чистит локальные blob'ы визита через clearVisitData.
 */
async function processFinalizeVisit(item: OutboxItem) {
  const orderId = item.order_id;
  const payload = item.payload as {
    completed_at?: string;
    note?: string;
    signature_refused?: boolean;
    refuse_reason?: string | null;
    visit_outcome?: string | null;
    outcome_reason?: string | null;
    target_status?: OrderStatus;
  };

  const targetStatus = payload.target_status;
  if (!targetStatus) {
    throw Object.assign(new Error('finalize_visit: payload.target_status отсутствует'), {
      code: '22023',
      status: 400,
    });
  }

  // Защита: если на локальной БД статус уже финальный (например,
  // предыдущий retry успешно прошёл, но мы не дождались ответа из-за
  // прерывания сети), просто чистим outbox и выходим. RPC всё равно
  // идемпотентен.
  const localOrder = await db.orders.get(orderId);
  if (localOrder && localOrder.status !== 'pending_completion' && localOrder.status !== 'in_progress') {
    // Уже финальный — нечего делать. Подчистим визит локально (на случай
    // если предыдущая попытка прервалась до этого шага) и выйдем.
    await clearVisitData(orderId);
    return;
  }

  const session = await db.visit_sessions.get(orderId);
  const signature = await db.signatures.get(orderId);

  // Собираем photo_urls из локальных server_url. Барьер media_finalize
  // гарантировал что все uploaded — но defensively проверяем ещё раз.
  const photos = await db.photos.where('order_id').equals(orderId).toArray();
  const notUploaded = photos.find((p) => p.upload_status !== 'uploaded' || !p.server_url);
  if (notUploaded) {
    // Барьер media_finalize должен был это поймать. Если мы тут — race
    // или нарушение порядка зависимостей. Retryable.
    throw new Error(`finalize_visit: фото ${notUploaded.id} не uploaded — барьер media_finalize нарушен`);
  }
  const photoUrls = photos.map((p) => ({
    url: p.server_url!,
    caption: p.caption,
  }));

  const signatureUrl = signature && !signature.refused
    ? (signature.server_url ?? null)
    : null;

  // resultSummary — агрегаты для отображения у диспетчера. Считаем
  // через ту же логику что в processVisitComplete (см. buildResultSummary).
  const resultSummary = await buildResultSummary(orderId, session, String(payload.note ?? ''));

  // Сериализуем checklist_data ровно как в processVisitComplete для
  // совместимости с visit_results readers (CompletionReport, AdminOrderDetail).
  const checklistData = Object.values(session?.results ?? {});

  const { data, error } = await supabase.rpc('finalize_visit_rpc', {
    p_order_id: orderId,
    p_target_status: targetStatus,
    p_completed_at: payload.completed_at ?? new Date().toISOString(),
    p_visit_outcome: payload.visit_outcome ?? null,
    p_outcome_reason: payload.outcome_reason ?? null,
    p_signature_refused: payload.signature_refused ?? false,
    p_refuse_reason: payload.refuse_reason ?? null,
    p_technician_note: String(payload.note ?? '').trim(),
    p_checklist_data: checklistData,
    p_photo_urls: photoUrls,
    p_signature_url: signatureUrl,
    p_result_summary: resultSummary,
  });

  if (error) throw error;

  // RPC вернул информацию о результате (idempotent или нет — пока
  // используем только для логов).
  if (import.meta.env.DEV) {
    console.debug(`[Кворум finalize_visit] ${orderId} → ${targetStatus}`, data);
  }

  // Обновляем локальный статус на финальный. Только сейчас, не раньше —
  // это инвариант I4 (локальное и серверное согласованы).
  const finalPatch = {
    status: targetStatus,
    completed_at: payload.completed_at ?? new Date().toISOString(),
    updated_at: new Date().toISOString(),
    result_summary: resultSummary,
  };
  await db.orders.update(orderId, finalPatch);
  useOrderStore.getState().updateLocalStatus(orderId, targetStatus, finalPatch);

  // 1.19.6 — Audit для финального перехода pending_completion → final.
  // Пара к событию, которое visitStore.markComplete/markFailedNoAccess/
  // abortVisit пишет на старте: «перешли в transit». Теперь — «вышли
  // из transit в финал». Диспетчер увидит обе точки в журнале.
  void (async () => {
    try {
      const { useAuthStore } = await import('../auth/authStore');
      const user = useAuthStore.getState().user;
      const { writeAuditEvent } = await import('../audit_log/api');
      void writeAuditEvent(user, {
        entity_type: 'work_order',
        entity_id: orderId,
        action: 'order_status_changed',
        payload: {
          from: 'pending_completion',
          to: targetStatus,
          visit_outcome: payload.visit_outcome ?? null,
          finalized_by_rpc: true,
        },
      });
    } catch (err) {
      // Audit — best-effort. Не валим успешный finalize_visit из-за
      // проблем с audit-логом.
      console.warn('[Кворум finalize_visit] audit write failed', err);
    }
  })();

  // Чистим временные данные визита: blob'ы фото, подпись, чек-лист
  // results, visit_session. server_url уже у каждой записи (через
  // uploadPhoto/uploadSignature которые писали в db до clearVisitData).
  // visit_results на сервере уже содержит полный snapshot. После
  // clearVisitData локально остаётся только сам work_order.
  await clearVisitData(orderId);
}

async function processItem(item: OutboxItem) {
  switch (item.type) {
    case 'create_order':
      await processCreateOrder(item);
      break;
    case 'create_order_items':
      // 1.19.7 — legacy. Новые заявки создаются через
      // 'create_order_with_items' (атомарный RPC). Handler оставлен
      // для уже записанных в Dexie на устройствах пилотов outbox-айтемов.
      await processCreateOrderItems(item);
      break;
    case 'create_order_with_items':
      await processCreateOrderWithItems(item);
      break;
    case 'update_work_order_item':
      await processUpdateWorkOrderItem(item);
      break;
    case 'status_change':
      await processStatusChange(item);
      break;
    case 'checklist_result':
      await processChecklistResult(item);
      break;
    case 'visit_complete':
      // 1.19.4 — legacy. Новые элементы НЕ создаются (см. visitStore).
      // Handler оставлен для уже накопленных на устройстве пилота.
      await processVisitComplete(item);
      break;
    case 'checklist_photo_upload':
      await processChecklistPhotoUpload(item);
      break;
    case 'checklist_photo_delete':
      await processChecklistPhotoDelete(item);
      break;
    case 'customer_upsert':
      await processCustomerUpsert(item);
      break;
    case 'customer_object_upsert':
      await processCustomerObjectUpsert(item);
      break;
    case 'link_object_to_order':
      await processLinkObjectToOrder(item);
      break;
    case 'invoice_upsert':
      await processInvoiceUpsert(item);
      break;
    case 'audit_event_insert':
      await processAuditEventInsert(item);
      break;
    case 'media_finalize':
      await processMediaFinalize(item);
      break;
    case 'finalize_visit':
      await processFinalizeVisit(item);
      break;
  }
}

// ── 1.17.0a — Offline-first business entities ───────────────────────────
// Каждый handler делает upsert по id; идемпотентный — если сервер уже
// видел эту запись (предыдущая попытка прошла), upsert пишет те же
// данные ещё раз без побочных эффектов.

async function processCustomerUpsert(item: OutboxItem) {
  const customer = item.payload.customer as Record<string, unknown> | undefined;
  if (!customer || typeof customer !== 'object') {
    throw new Error('customer_upsert: payload.customer missing');
  }
  const row = {
    id: customer.id,
    company_id: customer.company_id,
    name: customer.name,
    phone: customer.phone ?? null,
    email: customer.email ?? null,
    notes: customer.notes ?? null,
    created_by: customer.created_by ?? null,
    created_at: customer.created_at,
    updated_at: customer.updated_at,
  };
  const { error } = await supabase.from('customers').upsert(row, { onConflict: 'id' });
  if (error) throw error;
}

async function processCustomerObjectUpsert(item: OutboxItem) {
  // 1.17.1 — handler обрабатывает ОБА варианта:
  //   • payload.object        → customer_objects (фактическое оборудование)
  //   • payload.object_type   → object_types (словарь типов)
  // Раздельные таблицы с разной схемой, но обе идут под одним outbox
  // типом потому что у них одинаковый паттерн (offline-first upsert
  // через outbox).
  const objectType = item.payload.object_type as Record<string, unknown> | undefined;
  if (objectType && typeof objectType === 'object') {
    const typeRow = {
      id: objectType.id,
      company_id: objectType.company_id,
      name: objectType.name,
      icon: objectType.icon ?? null,
      is_active: objectType.is_active ?? true,
      created_at: objectType.created_at,
      updated_at: objectType.updated_at,
    };
    const { error } = await supabase.from('object_types').upsert(typeRow, { onConflict: 'id' });
    if (error) throw error;
    return;
  }

  const obj = item.payload.object as Record<string, unknown> | undefined;
  if (!obj || typeof obj !== 'object') {
    throw new Error('customer_object_upsert: payload.object or payload.object_type missing');
  }
  const row = {
    id: obj.id,
    company_id: obj.company_id,
    customer_id: obj.customer_id,
    type_id: obj.type_id,
    name: obj.name ?? null,
    serial_number: obj.serial_number ?? null,
    installed_at: obj.installed_at ?? null,
    warranty_until: obj.warranty_until ?? null,
    address: obj.address ?? null,
    notes: obj.notes ?? null,
    is_active: obj.is_active ?? true,
    created_by: obj.created_by ?? null,
    created_at: obj.created_at,
    updated_at: obj.updated_at,
  };
  const { error } = await supabase.from('customer_objects').upsert(row, { onConflict: 'id' });
  if (error) throw error;
}

async function processLinkObjectToOrder(item: OutboxItem) {
  const objectId = item.payload.object_id as string | null | undefined;
  if (!item.order_id) throw new Error('link_object_to_order: order_id missing');
  const { error } = await supabase
    .from('work_orders')
    .update({ object_id: objectId ?? null, updated_at: new Date().toISOString() })
    .eq('id', item.order_id);
  if (error) throw error;
}

async function processInvoiceUpsert(item: OutboxItem) {
  const invoice = item.payload.invoice as Record<string, unknown> | undefined;
  if (!invoice || typeof invoice !== 'object') {
    throw new Error('invoice_upsert: payload.invoice missing');
  }

  // 1.18.2 — Атомарный server-side upsert через RPC.
  // Раньше handler делал три отдельных запроса (upsert invoices +
  // delete invoice_items + insert новых) — между шагами могла
  // вклиниться чужая транзакция и потерять данные. RPC делает всё
  // в одной серверной транзакции с RLS-проверкой по company_memberships.
  //
  // См. supabase/migrations/20260530000001_upsert_invoice_with_items.sql
  const items = Array.isArray(invoice.items) ? (invoice.items as Record<string, unknown>[]) : [];

  // Items в RPC передаём отдельным параметром, не внутри p_invoice —
  // структура схемы (items НЕ колонка таблицы invoices) отражается в API.
  const invoicePayload: Record<string, unknown> = {
    id: invoice.id,
    order_id: invoice.order_id,
    company_id: invoice.company_id,
    created_by: invoice.created_by,
    status: invoice.status,
    payment_method: invoice.payment_method,
    total_amount: invoice.total_amount,
    paid_at: invoice.paid_at,
    created_at: invoice.created_at,
    updated_at: invoice.updated_at,
  };

  const itemsPayload = items.map((it) => ({
    id: it.id,
    title: it.title,
    quantity: it.quantity,
    unit_price: it.unit_price,
    total: it.total,
  }));

  const { error } = await supabase.rpc('upsert_invoice_with_items', {
    p_invoice: invoicePayload,
    p_items: itemsPayload,
  });
  if (error) throw error;
}

// ── 1.19.0 — Audit log offline ──────────────────────────────────────────
// audit_log событие пишется в локальный Dexie + outbox при действии
// пилота. Handler отправляет на сервер с upsert по id для
// идемпотентности — если retry прошёл после успешной попытки, повтор
// безопасен.
//
// После успеха обновляем локальную запись server_timestamp если
// сервер вернул отличающийся (синхронизация по часам). На практике
// клиентский timestamp может расходиться с серверным на секунды-минуты;
// для UI это не критично потому что мы сортируем по
// COALESCE(server_timestamp, client_timestamp).

async function processAuditEventInsert(item: OutboxItem) {
  const event = item.payload.event as Record<string, unknown> | undefined;
  if (!event || typeof event !== 'object') {
    throw new Error('audit_event_insert: payload.event missing');
  }
  const row = {
    id: event.id,
    company_id: event.company_id,
    entity_type: event.entity_type,
    entity_id: event.entity_id,
    actor_id: event.actor_id ?? null,
    actor_role: event.actor_role ?? null,
    action: event.action,
    payload: event.payload ?? null,
    client_timestamp: event.client_timestamp ?? null,
  };
  // Не передаём server_timestamp — сервер заполнит сам DEFAULT now().
  // Не передаём created_at — это серверная мета-колонка.
  const { data, error } = await supabase
    .from('audit_log')
    .upsert(row, { onConflict: 'id' })
    .select('id, server_timestamp, created_at')
    .single();
  if (error) throw error;
  // Обновляем локальный row server_timestamp от сервера.
  if (data && typeof data === 'object' && typeof event.id === 'string') {
    const rec = data as Record<string, unknown>;
    const serverTs = typeof rec.server_timestamp === 'string' ? rec.server_timestamp : null;
    const createdAt = typeof rec.created_at === 'string' ? rec.created_at : null;
    if (serverTs) {
      await db.audit_log.update(event.id, {
        server_timestamp: serverTs,
        ...(createdAt ? { created_at: createdAt } : {}),
      });
    }
  }
}

// ── 1.15.0 — Фото пунктов чек-листа ──────────────────────────────────────
// uploadChecklistPhoto: загружает blob в Storage по пути
//   checklist-photos/{order_id}/{photo_id}.jpg
// и INSERT в Supabase checklist_photos. Storage и DB row создаются вместе —
// если что-то падает между, при следующем retry мы делаем upsert blob в
// тот же путь (upsert: true) и проверяем существование row перед INSERT.

async function processChecklistPhotoUpload(item: OutboxItem) {
  const photoId = String(item.payload.photo_id ?? '');
  if (!photoId) throw new Error('checklist_photo_upload: photo_id missing');
  const photo = await db.checklist_photos.get(photoId);
  if (!photo) {
    // Фото удалили локально пока outbox-запись лежала. Считаем успехом.
    return;
  }
  if (photo.upload_status === 'uploaded' && photo.server_url) {
    // Уже загружено — пропускаем (возможно ретрай после успешного аплоада).
    return;
  }
  if (!photo.blob) {
    throw new Error('checklist_photo_upload: blob missing');
  }

  const path = `checklist-photos/${photo.order_id}/${photo.id}.jpg`;
  const uploadErr = await supabase.storage.from(STORAGE_BUCKET).upload(path, photo.blob, {
    upsert: true,
    contentType: 'image/jpeg',
  });
  if (uploadErr.error) throw uploadErr.error;

  // INSERT в checklist_photos. Если row уже существует (ретрай после
  // частичного успеха) — upsert по id предотвратит дубликат.
  const { error: insertErr } = await supabase.from('checklist_photos').upsert({
    id: photo.id,
    order_id: photo.order_id,
    item_id: photo.item_id,
    phase: photo.phase,
    storage_path: path,
    caption: photo.caption || null,
    taken_at: photo.taken_at,
    created_by: photo.created_by,
  }, { onConflict: 'id' });
  if (insertErr) throw insertErr;

  await db.checklist_photos.update(photo.id, {
    upload_status: 'uploaded',
    server_url: path,
    blob: null,
    attempts: photo.attempts + 1,
  });
}

async function processChecklistPhotoDelete(item: OutboxItem) {
  const photoId = String(item.payload.photo_id ?? '');
  const storagePath = item.payload.storage_path ? String(item.payload.storage_path) : null;
  if (!photoId) throw new Error('checklist_photo_delete: photo_id missing');

  // Удаляем DB-row (cascade не нужен — таблица плоская).
  const { error: dbErr } = await supabase.from('checklist_photos').delete().eq('id', photoId);
  if (dbErr) throw dbErr;

  // Удаляем файл в Storage если знаем путь. Игнорируем «file not found» —
  // если файла нет, это уже консистентно.
  if (storagePath) {
    await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]).catch(() => null);
  }
}

async function uploadPhoto(photo: { id: string; order_id: string; blob: Blob | null; caption: string; attempts: number; server_url: string | null }) {
  if (!photo.blob && photo.server_url) {
    await db.photos.update(photo.id, { upload_status: 'uploaded' });
    return;
  }
  if (!photo.blob) {
    throw new Error('Photo blob missing before upload');
  }

  const path = `photos/${photo.order_id}/${photo.id}.jpg`;
  const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, photo.blob, {
    upsert: true,
    contentType: 'image/jpeg',
  });
  if (error) throw error;

  // Store the canonical storage path (NOT a pseudo-public URL) so that
  // dispatcher / company-admin readers always issue a fresh signed URL from
  // a stable identifier. visit-media is a PRIVATE bucket — public URLs do
  // not resolve and parsing them back into a path is fragile.
  await db.photos.update(photo.id, {
    upload_status: 'uploaded',
    server_url: path,
    blob: null,
    attempts: photo.attempts + 1,
  });
}

async function uploadSignature(signature: { order_id: string; blob: Blob | null; refused: boolean; attempts?: number }) {
  if (signature.refused) {
    await db.signatures.update(signature.order_id, { upload_status: 'uploaded' });
    return;
  }
  if (!signature.blob) {
    throw new Error('Signature blob missing before upload');
  }

  const path = `signatures/${signature.order_id}.png`;
  const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, signature.blob, {
    upsert: true,
    contentType: 'image/png',
  });
  if (error) throw error;

  // Store the canonical storage path — see uploadPhoto for rationale.
  await db.signatures.update(signature.order_id, {
    upload_status: 'uploaded',
    server_url: path,
    blob: null,
    attempts: (signature.attempts ?? 0) + 1,
  });
}

async function uploadPendingMedia() {
  const photos = await db.photos.where('upload_status').anyOf(['local', 'failed']).sortBy('taken_at');
  for (const photo of photos) {
    try {
      await db.photos.update(photo.id, { upload_status: 'uploading' });
      await uploadPhoto(photo);
    } catch (error) {
      await db.photos.update(photo.id, {
        upload_status: 'failed',
        attempts: photo.attempts + 1,
        blob: photo.blob,
      });
      if (error instanceof Error) {
        console.warn(`Photo upload failed for ${photo.id}: ${error.message}`);
      }
    }
  }

  const signatures = await db.signatures.where('upload_status').anyOf(['local', 'failed']).sortBy('signed_at');
  for (const signature of signatures) {
    try {
      await db.signatures.update(signature.order_id, { upload_status: 'uploading' });
      await uploadSignature(signature);
    } catch (error) {
      await db.signatures.update(signature.order_id, {
        upload_status: 'failed',
        attempts: (signature.attempts ?? 0) + 1,
      });
      if (error instanceof Error) {
        console.warn(`Signature upload failed for ${signature.order_id}: ${error.message}`);
      }
    }
  }
}

async function uploadPendingMediaLocally() {
  const photos = await db.photos.where('upload_status').anyOf(['local', 'failed']).toArray();
  for (const photo of photos) {
    await db.photos.update(photo.id, {
      upload_status: 'uploaded',
      attempts: photo.attempts + 1,
    });
  }

  const signatures = await db.signatures.where('upload_status').anyOf(['local', 'failed']).toArray();
  for (const signature of signatures) {
    await db.signatures.update(signature.order_id, {
      upload_status: 'uploaded',
      attempts: (signature.attempts ?? 0) + 1,
    });
  }
}

async function patchUploadedMedia(orderId: string) {
  const uploadedPhotos = await db.photos
    .where('order_id')
    .equals(orderId)
    .filter((photo) => photo.upload_status === 'uploaded' && photo.server_url !== null)
    .toArray();
  const signature = await db.signatures.get(orderId);

  // Only update an existing visit_results row — never create an orphan one.
  // If visit_complete hasn't been processed yet, photo_urls will be patched
  // by processVisitComplete itself when it runs.
  const { data: existing } = await supabase
    .from('visit_results')
    .select('order_id')
    .eq('order_id', orderId)
    .single();
  if (!existing) return;

  // server_url stores the canonical storage path (see uploadPhoto/uploadSignature).
  // Readers (CompletionReport, AdminOrderDetail) resolve it via createSignedUrl() at view time.
  const { error } = await supabase.from('visit_results').update({
    photo_urls: uploadedPhotos.map((photo) => ({ url: photo.server_url!, caption: photo.caption })),
    signature_url: signature && !signature.refused ? signature.server_url : null,
  }).eq('order_id', orderId);
  if (error) throw error;
}

async function finalizeConfirmedVisits() {
  const sessions = await db.visit_sessions.toArray();
  for (const session of sessions.filter((entry) => entry.completed_at)) {
    // 1.19.4 — Legacy-логика. Новый протокол закрытия (finalize_visit
    // outbox-item + processFinalizeVisit handler) сам атомарно
    // записывает visit_results и сам вызывает clearVisitData в конце.
    // Здесь обрабатываем только заявки, оставшиеся в legacy режиме:
    // у них в outbox остался visit_complete (старый формат) или
    // вообще ничего и нужен явный patchUploadedMedia.
    //
    // Если у заявки в outbox есть finalize_visit — НЕ трогаем: новый
    // handler в processFinalizeVisit сам разберётся, в т.ч. с clearVisitData.
    // Иначе мы можем удалить blob'ы до того, как handler их использует.
    const hasNewFinalize = await db.outbox
      .where('order_id').equals(session.order_id)
      .filter((item) =>
        (item.type === 'finalize_visit' || item.type === 'media_finalize') &&
        item.status !== 'dead_letter'
      )
      .count();
    if (hasNewFinalize > 0) continue;

    const hasBlockingOutbox = await hasBlockingOutboxItems(session.order_id);
    if (hasBlockingOutbox) continue;

    const pendingPhotos = await db.photos
      .where('order_id')
      .equals(session.order_id)
      .filter((photo) => photo.upload_status !== 'uploaded')
      .count();

    const signature = await db.signatures.get(session.order_id);
    const signaturePending = signature ? signature.upload_status !== 'uploaded' && !signature.refused : false;
    if (pendingPhotos > 0 || signaturePending) continue;

    await reconcileTerminalOutbox(session.order_id);

    if (isSupabaseConfigured) {
      await patchUploadedMedia(session.order_id);
    }

    await clearVisitData(session.order_id);
  }
}

async function getEligibleOutboxItems() {
  const now = Date.now();
  const items = await db.outbox.orderBy('created_at').toArray();
  return items.filter((item) => {
    if (item.status === 'pending') return true;
    if (item.status === 'failed') {
      return !item.next_retry_at || new Date(item.next_retry_at).getTime() <= now;
    }
    if (item.status === 'processing') {
      return !item.locked_at || now - new Date(item.locked_at).getTime() > PROCESSING_STALE_MS;
    }
    return false;
  });
}

/**
 * 1.18.2 — Вычислить зависимости между outbox items.
 *
 * Возвращает Map: outbox.id → массив id записей которые должны
 * пройти ДО этой. Если массив не пустой — текущая запись пропускается
 * в этом flush'е, попробуем на следующем когда зависимости отработают.
 *
 * Концепция: некоторые outbox-записи имеют FK на ресурсы, которые
 * сами ещё в outbox. Отправлять "ребёнка" раньше "родителя" → FK
 * violation 23503 на сервере → запись летит в dead_letter, чего мы
 * хотим избежать.
 *
 * Правила:
 *  • create_order(customer_id=X) ждёт customer_upsert(id=X).
 *  • link_object_to_order(object_id=X) ждёт customer_object_upsert(id=X).
 *
 *  1.19.4 — Новые правила для протокола закрытия визита:
 *  • finalize_visit(order_id=X) ждёт ВСЕ outbox items с order_id=X
 *    которые ещё не отработали — кроме самого finalize_visit'а. Это
 *    гарантирует что к моменту RPC visit_results собирается из уже
 *    зафиксированных на сервере данных (work_order_items, invoice,
 *    storage files).
 *  • media_finalize(order_id=X) ждёт invoice_upsert(order_id=X) и
 *    update_work_order_item этой заявки — это не критично для
 *    корректности (media_finalize только проверяет storage), но
 *    задаёт более естественный порядок: сначала структурные данные,
 *    потом барьер на медиа, потом финализация.
 *
 * Что считается "незавершённой зависимостью":
 *  • pending или processing → блокирует (мы её собираемся отправлять).
 *  • failed с next_retry_at в будущем → блокирует (retry ещё впереди).
 *  • failed без next_retry_at (исчерпанный) → НЕ блокирует — он перейдёт
 *    в dead_letter, нет смысла держать всю очередь.
 *  • dead_letter → НЕ блокирует — там навсегда.
 *
 * ВАЖНО: если зависимость ушла в dead_letter, finalize_visit ВСЁ РАВНО
 * пойдёт. Это может означать что финал произойдёт без некоторого куска
 * данных (например, без счёта если invoice_upsert провалился permanently).
 * UI у диспетчера должен показать аномалию через dead_letter индикатор.
 * Альтернатива — блокировать finalize навсегда — оставила бы заявку
 * висеть в pending_completion вечно, что хуже.
 */
async function computeOutboxBlockingDeps(
  allItems: OutboxItem[],
): Promise<Map<string, string[]>> {
  const deps = new Map<string, string[]>();
  const now = Date.now();

  // Индексы для быстрого поиска зависимостей.
  const pendingCustomerUpsertById = new Map<string, OutboxItem>();
  const pendingObjectUpsertById = new Map<string, OutboxItem>();
  // 1.19.4 — для finalize_visit / media_finalize надо знать все
  // незакрытые items с тем же order_id.
  const pendingItemsByOrderId = new Map<string, OutboxItem[]>();

  const isStillPending = (it: OutboxItem): boolean => {
    if (it.status === 'pending' || it.status === 'processing') return true;
    if (it.status === 'failed') {
      // Будущий retry — блокирует, прошедший exhausted — нет.
      return it.next_retry_at !== null && new Date(it.next_retry_at).getTime() > now;
    }
    return false;
  };

  for (const it of allItems) {
    if (!isStillPending(it)) continue;
    if (it.type === 'customer_upsert') {
      const customer = it.payload.customer as Record<string, unknown> | undefined;
      if (customer && typeof customer.id === 'string') {
        pendingCustomerUpsertById.set(customer.id, it);
      }
    } else if (it.type === 'customer_object_upsert') {
      const obj = it.payload.object as Record<string, unknown> | undefined;
      if (obj && typeof obj.id === 'string') {
        pendingObjectUpsertById.set(obj.id, it);
      }
      // object_type-варианты тоже идут по этому же типу outbox — у них
      // payload.object_type. Они не блокируют ничего сейчас (object_type
      // самостоятелен), поэтому не индексируем.
    }
    // 1.19.4 — индексируем все pending items по order_id для finalize-зависимостей.
    if (it.order_id) {
      const list = pendingItemsByOrderId.get(it.order_id) ?? [];
      list.push(it);
      pendingItemsByOrderId.set(it.order_id, list);
    }
  }

  // Проходим по items и для каждого вычисляем блокирующие.
  for (const it of allItems) {
    if (it.status !== 'pending' && it.status !== 'failed') continue;
    const blockers: string[] = [];

    if (it.type === 'create_order') {
      const payloadOrder = it.payload as Record<string, unknown>;
      // Поле в payload — это сам WorkOrder; customer_id внутри него.
      const customerId = (payloadOrder.customer_id ?? null) as string | null;
      if (customerId && pendingCustomerUpsertById.has(customerId)) {
        const blocker = pendingCustomerUpsertById.get(customerId)!;
        if (blocker.id !== it.id) blockers.push(blocker.id);
      }
    } else if (it.type === 'link_object_to_order') {
      const objectId = it.payload.object_id as string | null | undefined;
      if (objectId && pendingObjectUpsertById.has(objectId)) {
        const blocker = pendingObjectUpsertById.get(objectId)!;
        if (blocker.id !== it.id) blockers.push(blocker.id);
      }
    } else if (it.type === 'finalize_visit') {
      // 1.19.4 — finalize_visit ждёт ВСЁ остальное для этой заявки.
      // К моменту RPC все work_order_items, invoice, storage должны
      // быть зафиксированы. Сам finalize_visit (если их несколько,
      // например после двойного нажатия) себя не блокирует.
      const siblings = pendingItemsByOrderId.get(it.order_id) ?? [];
      for (const sib of siblings) {
        if (sib.id === it.id) continue;
        blockers.push(sib.id);
      }
    } else if (it.type === 'media_finalize') {
      // 1.19.4 — media_finalize ждёт структурные данные заявки:
      // update_work_order_item, invoice_upsert, create_order, create_order_items.
      // 1.19.7 — также ждёт create_order_with_items (новый атомарный
      // тип, который пришёл на смену create_order + create_order_items).
      // НЕ ждёт status_change и customer/object upserts — они не влияют
      // на состояние медиа. И обязательно НЕ ждёт finalize_visit (это
      // приведёт к дедлоку, поскольку finalize_visit сам ждёт media_finalize).
      const siblings = pendingItemsByOrderId.get(it.order_id) ?? [];
      const blockerTypes: ReadonlySet<OutboxItemType> = new Set([
        'create_order',
        'create_order_items',
        'create_order_with_items',
        'update_work_order_item',
        'invoice_upsert',
      ]);
      for (const sib of siblings) {
        if (sib.id === it.id) continue;
        if (blockerTypes.has(sib.type)) {
          blockers.push(sib.id);
        }
      }
    }

    if (blockers.length > 0) {
      deps.set(it.id, blockers);
    }
  }

  return deps;
}

/**
 * 1.19.8 — Lightweight разлок stale processing записей.
 *
 * Вызывается перед каждым flushOutbox: смотрит processing-записи с
 * locked_at старше PROCESSING_STALE_MS и переводит обратно в pending.
 *
 * Это «дешёвый кусочек» из runSyncRecoveryPass — не оживляет dead_letter,
 * не сбрасывает retry budget. Только разлок зависших processing.
 *
 * Безопасна к частому вызову: одно where-query и узкий filter. Если
 * stale нет — никаких записей.
 */
export async function unlockStaleProcessing(): Promise<void> {
  const now = Date.now();
  const processingItems = await db.outbox.where('status').equals('processing').toArray();
  const stale = processingItems.filter(
    (item) => !item.locked_at || now - new Date(item.locked_at).getTime() > PROCESSING_STALE_MS,
  );
  for (const item of stale) {
    await db.outbox.update(item.id, {
      status: 'pending',
      locked_at: null,
      last_error: 'Recovered: stale processing lock cleared',
    });
  }
}

export async function runSyncRecoveryPass(): Promise<void> {
  const now = Date.now();

  // ── Step 1: Unlock stale 'processing' outbox items ───────────────────────
  // Items stuck in 'processing' mean the previous flush was interrupted
  // (app crash, tab kill, network timeout). Reset them to 'pending' so the
  // subsequent flushOutbox() call can retry them cleanly.
  //
  // 1.19.8: вынесено в отдельную unlockStaleProcessing() — она же вызывается
  // в начале каждого runFlush. Здесь оставлен прежний код для обратной
  // совместимости (recovery pass должен быть самодостаточным).
  const processingItems = await db.outbox.where('status').equals('processing').toArray();
  const staleProcessing = processingItems.filter(
    (item) => !item.locked_at || now - new Date(item.locked_at).getTime() > PROCESSING_STALE_MS,
  );
  for (const item of staleProcessing) {
    await db.outbox.update(item.id, {
      status: 'pending',
      locked_at: null,
      last_error: 'Recovered: stale processing lock cleared',
    });
  }

  // ── Step 2: Revive exhausted retryable items ────────────────────────────
  // Items in 'dead_letter' with category retryable/unknown — это те что
  // достигли maxAttempts. Manual recovery trigger — explicit user intent
  // to retry. Возвращаем в pending, сбрасываем счётчик.
  //
  // 1.18.0 — Permanent ошибки НЕ оживляем: их retry бесполезен (RLS,
  // FK, validation). Они требуют ручного вмешательства (изменения
  // данных или прав), которое выходит за рамки автоматического recovery.
  // Чтобы их оживить, пилот должен использовать "Удалить и пересоздать"
  // в UI dead-letter (придёт в следующем подрелизе).
  const failedItems = await db.outbox.where('status').equals('failed').toArray();
  const exhaustedFailed = failedItems.filter(
    (item) => item.next_retry_at === null && item.attempts >= MAX_OUTBOX_ATTEMPTS,
  );
  for (const item of exhaustedFailed) {
    await db.outbox.update(item.id, {
      status: 'pending',
      attempts: 0,
      next_retry_at: null,
      locked_at: null,
      last_error: 'Recovered: retry budget reset by user action',
      last_error_category: undefined,
    });
  }

  // Dead-letter retryable/unknown — тоже можем оживить вручную.
  const deadLetterItems = await db.outbox.where('status').equals('dead_letter').toArray();
  const revivableDeadLetter = deadLetterItems.filter(
    (item) => item.last_error_category !== 'permanent',
  );
  for (const item of revivableDeadLetter) {
    await db.outbox.update(item.id, {
      status: 'pending',
      attempts: 0,
      next_retry_at: null,
      locked_at: null,
      last_error: 'Recovered from dead_letter by user action',
      last_error_category: undefined,
    });
  }

  // ── Step 3: Reset stuck 'uploading' media ────────────────────────────────
  // Photos/signatures in 'uploading' state mean the upload started but the
  // app was killed before the status was updated. Reset to 'local' if the
  // blob is still present (retryable), or 'failed' if blob is gone.
  const stuckPhotos = await db.photos.where('upload_status').equals('uploading').toArray();
  for (const photo of stuckPhotos) {
    await db.photos.update(photo.id, {
      upload_status: photo.blob ? 'local' : 'failed',
    });
  }

  const stuckSignatures = await db.signatures.where('upload_status').equals('uploading').toArray();
  for (const sig of stuckSignatures) {
    await db.signatures.update(sig.order_id, {
      upload_status: sig.blob ? 'local' : 'failed',
    });
  }

  // ── Step 4: Reconcile terminal orders ────────────────────────────────────
  // For orders that are locally done, clean up any non-blocking stale outbox
  // entries (checklist_result, status_change) that were never processed.
  // These accumulate when the visit completes while offline and the outbox
  // flush happens to skip non-blocking items.
  const allOrderIds = Array.from(
    new Set((await db.outbox.toArray()).map((item) => item.order_id)),
  );
  for (const orderId of allOrderIds) {
    await reconcileTerminalOutbox(orderId);
  }
}

async function flushLocalOnly() {
  const items = await db.outbox.orderBy('created_at').toArray();
  for (const item of items) {
    if (item.type === 'visit_complete') {
      await markOrderDoneLocally(item);
    }
    await db.outbox.delete(item.id);
  }
  await uploadPendingMediaLocally();
}

async function runFlush() {
  if (!navigator.onLine && isSupabaseConfigured) {
    useSyncStore.getState().setStatus('offline');
    await refreshSyncMeta();
    return;
  }

  // 1.19.8 — Lightweight unlock stale processing перед каждым flush.
  //
  // Проблема: если предыдущий flush был прерван (вкладка убита, телефон
  // ушёл в сон, network timeout), некоторые items остались в статусе
  // 'processing' с заполненным locked_at. На следующем flush они НЕ
  // попадают в getEligibleOutboxItems (там фильтр where status='pending'),
  // и блокируют зависимых (computeOutboxBlockingDeps считает processing
  // как live dependency). В итоге очередь не дренируется — пилот видит
  // «синхронизацию приходится дожимать вручную».
  //
  // Раньше unlock делал только runSyncRecoveryPass — она вызывается
  // лишь на событие online или ручной recovery. Если сеть была всегда,
  // а одна запись застряла — recovery не срабатывал.
  //
  // Сейчас перед каждым flush мы дёшево (1 select + bulkPut) сбрасываем
  // зависшие более минуты processing-записи обратно в pending. Это не
  // мешает нормальному processing (live запись с locked_at < 60s остаётся).
  await unlockStaleProcessing();

  await refreshSyncMeta();
  // If refreshSyncMeta found nothing pending it already called markSynced() —
  // bail out early so we don't override that with 'syncing' and cause a
  // one-frame SyncBar flash on every screen navigation.
  if (useSyncStore.getState().pendingCount === 0) return;
  useSyncStore.getState().setStatus(isSupabaseConfigured ? 'syncing' : 'pending');

  if (!isSupabaseConfigured) {
    const localItems = await db.outbox.orderBy('created_at').toArray();
    await flushLocalOnly();
    await refreshSyncMeta();
    if (localItems.length > 0) {
      useSyncStore.getState().showToast('Данные сохранены локально ✓');
    }
    return;
  }

  const items = await getEligibleOutboxItems();

  // 1.18.2 — Dependency-aware ordering.
  //
  // FIFO по created_at почти всегда дает правильный порядок (мы и так
  // enqueue customer_upsert ДО create_order для одной транзакции
  // диспетчера). Но если customer_upsert упал в первом цикле и стал
  // failed/dead_letter — create_order с этим customer_id уйдёт раньше
  // и упадёт по FK 23503.
  //
  // Поэтому перед обработкой каждого item смотрим есть ли в очереди
  // НЕзавершённые зависимости. Если есть — пропускаем эту итерацию
  // (item остаётся pending), на следующий flush попробуем снова уже
  // когда зависимость пройдёт.
  //
  // Зависимости (правила):
  //  • create_order             — зависит от customer_upsert с тем же
  //                               customer_id (если он есть в outbox).
  //  • link_object_to_order     — зависит от customer_object_upsert с
  //                               тем object_id.
  //  • invoice_upsert           — без зависимостей (заявка может
  //                               отсутствовать на сервере — invoice
  //                               существует независимо в этой схеме).
  //
  // Все pending/processing items блокируют зависимых; dead_letter — НЕ
  // блокирует (там уже навсегда), failed с next_retry_at в будущем —
  // блокирует, потому что retry ещё впереди.
  const allItems = await db.outbox.toArray();
  const blockingDeps = await computeOutboxBlockingDeps(allItems);

  let syncedCount = 0;
  for (const item of items) {
    // Проверка deps: если есть незакрытая зависимость, пропускаем.
    const blockedBy = blockingDeps.get(item.id);
    if (blockedBy && blockedBy.length > 0) {
      // Логируем впервые, но не каждую итерацию — иначе console
      // забивается. Помечаем последний фактор в last_error info.
      console.debug(
        `[Кворум outbox] ${item.type}/${item.id} waiting for ${blockedBy.length} dep(s)`,
      );
      continue;
    }

    try {
      await db.outbox.update(item.id, {
        status: 'processing',
        locked_at: new Date().toISOString(),
        last_error: null,
      });
      await processItem(item);
      await db.outbox.delete(item.id);
      syncedCount++;
    } catch (error) {
      // 1.18.0 — Классификация ошибки. Permanent сразу в dead_letter,
      // retryable — с экспоненциальным бэкоффом до maxAttempts.
      const { category, message, maxAttempts } = classifyError(error);
      const nextAttempts = item.attempts + 1;

      if (category === 'permanent') {
        // Любое количество ретраев не поможет. Сразу dead_letter.
        await db.outbox.update(item.id, {
          status: 'dead_letter',
          attempts: nextAttempts,
          last_error: message,
          last_error_category: 'permanent',
          next_retry_at: null,
          locked_at: null,
        });
        console.error(`[Кворум outbox] ${item.type}/${item.id} → dead_letter (permanent): ${message}`);
      } else if (nextAttempts >= maxAttempts) {
        // Исчерпали попытки на retryable → тоже dead_letter, но
        // с другим last_error_category чтобы UI мог разделить
        // «совсем не пройдёт» vs «не получилось много раз».
        await db.outbox.update(item.id, {
          status: 'dead_letter',
          attempts: nextAttempts,
          last_error: `После ${nextAttempts} попыток: ${message}`,
          last_error_category: category,
          next_retry_at: null,
          locked_at: null,
        });
        console.error(`[Кворум outbox] ${item.type}/${item.id} → dead_letter (exhausted ${nextAttempts} attempts): ${message}`);
      } else {
        // Retryable, есть ещё попытки.
        await db.outbox.update(item.id, {
          status: 'failed',
          attempts: nextAttempts,
          last_error: message,
          last_error_category: category,
          next_retry_at: new Date(Date.now() + getRetryDelay(item.attempts)).toISOString(),
          locked_at: null,
        });
      }
    }
  }

  await uploadPendingMedia();
  await finalizeConfirmedVisits();
  await refreshSyncMeta();

  if (syncedCount > 0) {
    useSyncStore.getState().showToast(`Сохранено на сервере ✓`);
  }
}

export async function flushOutbox() {
  if (flushPromise) {
    return flushPromise;
  }

  flushPromise = runFlush().finally(async () => {
    flushPromise = null;
    await refreshSyncMeta();
  });

  return flushPromise;
}

/**
 * 1.19.8 — Запуск/остановка фонового авто-flush таймера.
 *
 * Раз в BACKGROUND_FLUSH_INTERVAL_MS (30 сек) проверяет, есть ли в outbox
 * pending/failed записи. Если есть и устройство онлайн — вызывает
 * flushOutbox. Это устраняет основную жалобу пилотов «синхронизацию
 * приходится дожимать вручную»: если первый flush после восстановления
 * сети не дренировал очередь до конца, второй проход через 30 секунд
 * добьёт остаток без участия пользователя.
 *
 * Не дублирует уже запущенный flush — flushOutbox сам guard'ит через
 * flushPromise singleton. Когда очередь пуста — таймер тратит один
 * Dexie count() и больше ничего.
 */
function startBackgroundFlushLoop() {
  if (backgroundFlushTimer !== null) return;
  backgroundFlushTimer = window.setInterval(async () => {
    try {
      if (!navigator.onLine) return;
      // Дёшево: один count() по status-индексу.
      const pendingOrFailed = await db.outbox
        .where('status')
        .anyOf(['pending', 'failed', 'processing'])
        .count();
      if (pendingOrFailed === 0) return;
      // unlockStaleProcessing уже зашит внутрь runFlush, поэтому
      // зависшие processing будут разлокированы автоматически.
      void flushOutbox();
    } catch (err) {
      // Не валим таймер из-за разовой проблемы с Dexie.
      console.debug('[Кворум auto-retry] background flush skipped', err);
    }
  }, BACKGROUND_FLUSH_INTERVAL_MS);
}

function stopBackgroundFlushLoop() {
  if (backgroundFlushTimer !== null) {
    window.clearInterval(backgroundFlushTimer);
    backgroundFlushTimer = null;
  }
}

export function installSyncTriggers() {
  const onOnline = () => {
    // 1.19.2 — Перед первым flush делаем recovery pass. Stuck 'uploading'
    // фото и подпись после потери сети/убийства вкладки висят в этом
    // статусе и не попадают в uploadPendingMedia (он смотрит только
    // local/failed). Recovery возвращает их в local. Без этого — фото
    // ждали ручного нажатия «синхронизировать», что было неудобно.
    void runSyncRecoveryPass().then(() => scheduleFlush(100));
    // Повторный проход через ~4 сек. Зачем: runFlush() сначала
    // отправляет JSON outbox (включая visit_complete), потом
    // uploadPendingMedia (фото + подпись), потом finalizeConfirmedVisits
    // которая patches visit_results.photo_urls/signature_url. Если медиа
    // не успели за первый проход (большие файлы, медленный канал) —
    // visit_results остаётся с photo_urls=[] и signature_url=null, и
    // диспетчер видит «фото нет» хотя оно загружается. Повторный flush
    // догоняет: на втором проходе медиа уже uploaded, finalizeConfirmedVisits
    // успешно патчит.
    window.setTimeout(() => {
      if (!navigator.onLine) return;
      if (useSyncStore.getState().pendingCount === 0) return;
      scheduleFlush(0);
    }, 4000);
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      scheduleFlush(150);
      // 1.19.8 — также пытаемся дожать очередь при возврате во вкладку.
      // Если пилот зашёл в SyncQueue и вышел из приложения на 5 минут,
      // background loop успел поработать, но возврат во вкладку — самый
      // явный сигнал что пилот ожидает прогресса. Дополнительный recovery
      // pass дешёвый (DB lookup на застрявшие processing).
      if (navigator.onLine) {
        void runSyncRecoveryPass();
      }
    }
  };

  // 1.19.8 — Background flush loop. Каждые 30 сек проверяет очередь и
  // дожимает её, если есть работа. Это устраняет необходимость ручного
  // «Запустить синхронизацию» когда первый flush после `online` не
  // дренировал очередь до конца (зависшие фото-uploads, временные
  // network blip'ы).

  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibility);
  void refreshSyncMeta();
  scheduleFlush(250);
  startBackgroundFlushLoop();

  return () => {
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibility);
    if (scheduledFlushTimer !== null) {
      window.clearTimeout(scheduledFlushTimer);
      scheduledFlushTimer = null;
    }
    stopBackgroundFlushLoop();
  };
}
